import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { allProducts } from '../../mocks/products';
import SchemaMarkup from '../../components/base/SchemaMarkup';

const categories = ['All', 'Industrial', 'Professional', 'Desktop'];

const productsSchema = [
  {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "CO2 Laser Machines - Redsail Laser Malta",
    "description": "Professional laser cutting and engraving solutions for wood, acrylic, leather, fabric and more. Industrial, professional, and desktop machines available.",
    "url": "https://redsaillaser-malta.com/products"
  },
  {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Redsail Laser Machine Products",
    "description": "Complete range of CO2 laser cutting and engraving machines",
    "itemListElement": allProducts.map((product, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Product",
        "name": product.title,
        "description": product.description,
        "image": product.image,
        "brand": {
          "@type": "Brand",
          "name": "Redsail"
        },
        "category": product.category,
        "url": `https://redsaillaser-malta.com/product/${product.id}`
      }
    }))
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://redsaillaser-malta.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Products",
        "item": "https://redsaillaser-malta.com/products"
      }
    ]
  }
];

const ProductsPage = () => {
  const [activeCategory, setActiveCategory] = useState('All');

  const filteredProducts =
    activeCategory === 'All'
      ? allProducts
      : allProducts.filter((p) => p.category === activeCategory);

  return (
    <div className="min-h-screen bg-white">
      <SchemaMarkup schema={productsSchema} />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <span className="text-emerald-500 font-semibold text-sm tracking-wider uppercase">
            Our Products
          </span>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mt-3 mb-6">
            CO2 Laser Machines
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Professional laser cutting and engraving solutions for wood, acrylic, leather, fabric and more. 
            Choose from our complete range of industrial, professional, and desktop machines.
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-white border-b border-gray-200 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-center gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-6 py-3 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  activeCategory === cat
                    ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/25'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-product-shop>
            {filteredProducts.map((product) => (
              <Link
                key={product.id}
                to={`/product/${product.id}`}
                className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500"
              >
                <div className="relative h-64 overflow-hidden bg-gray-100">
                  {product.badge && (
                    <span className="absolute top-4 left-4 z-10 px-3 py-1 bg-emerald-500 text-white text-xs font-semibold rounded-full">
                      {product.badge}
                    </span>
                  )}
                  <img
                    src={product.image}
                    alt={product.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                </div>
                <div className="p-6">
                  <span className="text-emerald-500 text-sm font-medium">
                    {product.subtitle}
                  </span>
                  <h3 className="text-xl font-bold text-gray-900 mt-1 mb-2 group-hover:text-emerald-600 transition-colors">
                    {product.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {product.description}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {product.specs.map((spec, i) => (
                      <span
                        key={i}
                        className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
                      >
                        {typeof spec === 'string' ? spec : `${spec.label}: ${spec.value}`}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center text-emerald-500 font-semibold text-sm">
                    <span className="whitespace-nowrap">View Details</span>
                    <i className="ri-arrow-right-line ml-2 group-hover:translate-x-2 transition-transform"></i>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-emerald-500">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">
            Need Help Choosing the Right Machine?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Our experts are ready to help you find the perfect laser machine for your needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="px-8 py-4 bg-white text-emerald-600 font-semibold rounded-full hover:bg-gray-100 transition-all whitespace-nowrap"
            >
              Contact Us
            </Link>
            <a
              href="tel:+35699446552"
              className="px-8 py-4 bg-emerald-600 text-white font-semibold rounded-full hover:bg-emerald-700 transition-all whitespace-nowrap"
            >
              Call +356 9944 6552
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProductsPage;
